import { useQuery } from "@tanstack/react-query";
import { Tooltip } from "antd";
import CustomDateFilter from "common/components/CustomDateFilter";
import CustomSelect from "common/components/CustomSelect";
import SearchComponent from "common/components/SearchComponent";
import {
  StyledAntdTable,
  StyledButton
} from "common/components/StyledElements";
import APIConstants from "common/constants/APIConstants";
import { useAuthContext } from "common/contexts/AuthContext";
import { formatQuery, getUserRole } from "common/utils";
import { RMPM_ROLE } from "common/constants";
import { api } from "common/utils/APIMethods";
import dayjs from "dayjs";
import { useMemo, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";


const MainContainer = styled.div`
  margin: 20px auto 0;
  width: 98%;
`;

const HeaderContainer = styled.div`
  display: flex;
  column-gap: 16px;
  margin-bottom: 16px;
  height: 56px;
  @media screen and (max-width: 1279px) {
    height: fit-content;
    flex-wrap: wrap;
    row-gap: 8px;
    margin-bottom: 10px;
  }
`;

const RightContainer = styled.div`
  display: flex;
  gap: 10px;
`;

const AddStyledButton = styled(StyledButton)`
  padding: 12px;
`;


const TypeDiv = styled.div`
  display: flex;
  justify-content: center;
  background-color: ${(props) => props.backgroundColor || "white"};
`;

const getUrlWithOrderStatusIds = (startDate, endDate, queryParams) => {
  const params = new URLSearchParams();

  if (queryParams?.query) {
    params.append("query", queryParams.query);
  }

  if (startDate) {
    params.append("startDate", startDate);
  }

  if (endDate) {
    params.append("endDate", endDate);
  }

  if (queryParams?.status) {
    params.append("status", queryParams.status);
  }

  if (queryParams?.companyIds?.length) {
    queryParams.companyIds.forEach((id) => {
      params.append("companyIds", id);
    });
  }

  if (queryParams?.locationIds?.length) {
    queryParams.locationIds.forEach((id) => {
      params.append("locationIds", id);
    });
  }

  const baseUrl = APIConstants.getAllPurchaseOrders;
  const paramString = params.toString();

  return paramString ? `${baseUrl}?${paramString}` : baseUrl;
};

const AllSaleList = () => {
  const { authConfig } = useAuthContext();
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    currentPage: 0,
    pageSize: 20
  });
  const [selectedDates, setSelectedDates] = useState([
    dayjs().subtract(7, "days").format("YYYY-MM-DD"),
    dayjs().format("YYYY-MM-DD")
  ]);
  const [companyIds,setCompanyIds] = useState([]);
  const [locationIds, setLocationIds] = useState([]);

  const navigate = useNavigate();

  const { state } = useLocation();
  const { companyIds : prefilledCompanyIds, locationIds : prefilledLocationIds, filter=null, selectedDates : prefilledDates} = state || {};
  
  const isRoleMasterAdmin = getUserRole(authConfig) === RMPM_ROLE.masterAdmin


  const {
    data: saleList = [],
    isLoading: isSaleListLoading
  } = useQuery({
    queryKey: [
      "saleList",
      searchText,
      pagination.currentPage,
      pagination.pageSize,
      selectedDates,
      companyIds,
      locationIds
    ],
    queryFn: () =>
      api(
        {
          url: getUrlWithOrderStatusIds(selectedDates[0], selectedDates[1], {
            query: searchText ? formatQuery(searchText) : null,
             ...(isRoleMasterAdmin && {
                companyIds,
                locationIds
              }),
              ...(filter === "PENDING" && {
                status: "YET_TO_BE_DISPATCHED"
              })
          })
        },
        authConfig
      )
  });

  const typeColorMapping =useMemo(()=>{
    const typeColor = {
    IN_TRANSIT: { background: "#c9b3e2ff" },
    YET_TO_BE_DISPATCHED: { background: "#4ecdc4" },
    DELIVERED: { background: "#ffd97d" }
   };
   return typeColor;
  },[]);

  const SaleColumns = useMemo(() => {
   
    const memoColumns = [
      {
        title: "PO Number",
        render: (item) => item?.poNumber,
        width: "10%"
      },
      {
        title: "CAS Number",
        render: (item) => item?.casNumber,
        width: "10%"
      },
      {
        title: "Material Code",
        render : (item) => item?.materialCode,
        width : "10%"
      },
      {
        title : "Material Name",
        render : (item) => item?.materialName,
        width : "10%"
      },
      {
        title: "Vendor Name",
        render: (item) => item?.vendorName,
        width: "10%"
      },
      {
        title: "Quantity",
        render: (item) => item?.quantity,
        width: "10%"
      },
      {
        title: "Total",
        render: (item) => item?.total,
        width: "10%"
      },
       {
        title: "Status",
        render: (item) => (
          <TypeDiv
            backgroundColor={
              typeColorMapping[item?.scmPoStatus?.status]?.background
            }
          >
            <span>{item?.scmPoStatus?.status.replaceAll("_", " ")}</span>
          </TypeDiv>
        ),
        width: "10%"
      }
    ].filter(Boolean);
    return memoColumns;
  },[typeColorMapping]);

    const { data : companies = [] } = useQuery({
            queryKey : ["companies"],
            queryFn : () =>
                api(
                    {
                        url : APIConstants.getCompanies
                    },
                    authConfig
                ),
            enabled: isRoleMasterAdmin
      });
    
    const companyOptions = companies?.map((company)=>(
      {
          value : company.companyId,
          label : company.businessName
      }));

  
      useEffect(() => {
        if (companies.length > 0) {
          if (prefilledCompanyIds && prefilledCompanyIds.length > 0) {
            setCompanyIds(prefilledCompanyIds);
          } else {
            setCompanyIds(companies.map(c => c.companyId));
          }
        }
      }, [companies, prefilledCompanyIds]);
  
    const { data : locations = [] } = useQuery({
            queryKey : ["locations",companyIds],
            queryFn : () =>
                api(
                    {
                        url : APIConstants.getCompanyLocations,
                        params : { companyIds }
                    },
                    authConfig
                ),
            enabled : companyIds.length > 0
    });
      
    const locationOptions = locations.map((location)=>(
        {
            value : location.id,
            label : location.depot
        }
    ));
  
    useEffect(() => {
    if (prefilledLocationIds && prefilledLocationIds.length > 0) {
      setLocationIds(prefilledLocationIds);
    } else if (locations.length > 0) {
      setLocationIds(locations.map(l => l.id));
    }
  }, [locations, prefilledLocationIds]);

  useEffect(() => {
    if (prefilledDates && prefilledDates.length === 2) {
      setSelectedDates(prefilledDates);
    }
  }, [prefilledDates]);

  return (
    <MainContainer>
      <HeaderContainer>
        <SearchComponent
          placeholder="Search by Product Name or PO Number"
          searchText={searchText}
          setSearchText={setSearchText}
          pagination={pagination}
          setPagination={setPagination}
        />
        {isRoleMasterAdmin && 
          <CustomSelect
            menuTitle="Filter Company"
            filterData={companyOptions.map(c => ({
              id: c.value,
              label: c.label
            }))}
            selectedOption={companyIds}
            setSelectedOption={(data) => {
              setCompanyIds(data);
              setLocationIds([]);
            }}
            setPagination={setPagination}
            pagination={pagination}
            isMultiSelect
            containerStyle={{ width: 200 }}
            defaultStatuses={
              companyOptions.length > 0
                ? companyOptions.map(c => c.value)
                : []
            }
            type="Company"
          />
        }
        {isRoleMasterAdmin &&
            <CustomSelect
              menuTitle="Filter Location"
              filterData={locationOptions.map(l => ({
                id: l.value,
                label: l.label
              }))}
              selectedOption={locationIds}
              setSelectedOption={(data) => {
                setLocationIds(data);
              }}
              setPagination={setPagination}
              pagination={pagination}
              isMultiSelect
              containerStyle={{ width: 200 }}
              defaultStatuses={
                locationOptions.length > 0
                  ? locationOptions.map(l => l.value)
                  : []
              }
              type="Location"
            />
          }
        <RightContainer>
          <CustomDateFilter
            pagination={pagination}
            setPagination={setPagination}
            selectedDates={selectedDates}
            setSelectedDates={(data) => {
              setSelectedDates(data);
            }}
          />
          <Tooltip
            title="Select Location"
            trigger={["hover"]}
            color="rgba(0, 0, 0, 0.70)"
            styles={{
              body: {
                display: "flex",
                alignItems: "center",
                whiteSpace: "nowrap",
                fontFamily: "Poppins"
              },
              root: {
                borderRadius: "12px"
              }
            }}
            placement="topLeft"
          >
            {/* <CustomSelect
              menuTitle={t("filter location")}
              isLoading={isLocationsLoading}
              filterData={filteredLocations}
              selectedOption={selectedLocations}
              setSelectedOption={(data) => {
                setSelectedLocations(data);
              }}
              type="Locations"
              setPagination={setPagination}
              pagination={pagination}
              isMultiSelect
              containerStyle={{ width: 183 }}
              defaultStatuses={inventoryLocations?.map((item) => item.id)}
            /> */}
          </Tooltip>
          {isRoleMasterAdmin || <AddStyledButton
            type="primary"
            onClick={() => navigate("add")}
          >
            Add PO
          </AddStyledButton> }
        </RightContainer>
      </HeaderContainer>
      <StyledAntdTable
        columns={SaleColumns}
        isLoading={isSaleListLoading}
        dataSource={saleList || []}
        locale={{ emptyText: "No Purchase Orders Found" }}
        pagination={{
          current: pagination.currentPage + 1,
          pageSize: pagination.pageSize,
          position: ["none", "none"]
        }}
        onRow={(order) => ({
          onClick: (e) => {
            e.stopPropagation();
            if (!order?.id) return;

            navigate(`/scm-po/details/${order.id}`, {
              state: order
            });
          }
        })}
      />
      {/* <PaginationComponent
        pagination={pagination}
        setPagination={setPagination}
        totalElements={saleList?.totalElements}
        totalPages={Math.ceil(
          (saleList?.totalElements ?? 0) / pagination.pageSize
        )}
        heading="products"
      />{" "} */}
    </MainContainer>
  );
};

export default AllSaleList;